import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "motion/react";
import { useAuth } from "../../../utils/Auth";
import API from "../../../API";
import { useTimezoneSelect, allTimezones } from "react-timezone-select";
import RestoreQRThumbnail from "./RestoreQRThumbnail";
import { allQrTypes } from "../../../utils/hooks/useTypeHeaderIcon";
import { variants, transition } from "../variants";
import styles from "../Settings.module.css";

export default function QrArchive() {
    const { userObj, currentUser, deletedQrs, liquidQRs } = useAuth();
    const { updateUserObject, updateQR } = API;

    const [sonarLength, setSonarLength] = useState("600000");
    const [selectedTimezone, setSelectedTimezone] = useState("");
    const [message, setMessage] = useState(null);

    const { options, parseTimezone } = useTimezoneSelect("original", allTimezones);

    useEffect(() => {
        if (userObj) {
            if (userObj?.sonar_length) {
                setSonarLength(String(userObj.sonar_length));
            }
            const fallbackTz = Intl.DateTimeFormat().resolvedOptions().timeZone;
            setSelectedTimezone(userObj?.timezone?.value || fallbackTz);
        }
    }, [userObj]);

    const setTempMessage = (text, type = "info") => {
        setMessage({ text, type });
        setTimeout(() => setMessage(null), 2500);
    };

    const getTypeHeader = (qrType) => {
        if (qrType === "liquid_link") {
            return "Liquid Link";
        }
        const found = allQrTypes.find((t) => t.type === qrType);
        return found?.header || qrType;
    };

    const handleUpdateSonar = async (e) => {
        const value = e.target.value;
        setSonarLength(value);
        try {
            await updateUserObject(currentUser.uid, { sonar_length: parseInt(value, 10) });
            setTempMessage("Sonar length updated", "success");
        } catch (err) {
            console.error(err);
            setTempMessage("Failed to update sonar length", "error");
        }
    };

    const handleUpdateTimezone = async (e) => {
        const zone = parseTimezone(e.currentTarget.value);
        setSelectedTimezone(zone.value);
        try {
            await updateUserObject(currentUser.uid, {
                timezone: { value: zone.value, label: zone.label, offset: zone.offset },
            });
            setTempMessage("Timezone updated", "success");
        } catch (err) {
            console.error(err);
            setTempMessage("Failed to update timezone", "error");
        }
    };

    const handleRestore = async (qrId) => {
        try {
            await updateQR(qrId, {qrDataType: "", qrFiles: [], data: {active: true}});
            setTempMessage("QR restored", "success");
        } catch (error) {
            console.error("Error reinstating QR: ", error.message);
            setTempMessage("Failed to restore QR", "error");
        }
    };

    if (!userObj || !currentUser) return null;

    const capacity = userObj?.plan?.liquid_qrs ?? 0;
    const canRestore = (liquidQRs?.length ?? 0) < capacity;

    return (
        <div className={styles.section}>
            <h3 className={styles.sectionTitle}>QR Archive</h3>
            <motion.div className={styles.sectionContent}>
                {/* Sonar Duration */}
                <div className={styles.fieldGroup}>
                    <label className={styles.fieldLabel}>Sonar Mode Duration</label>
                    <div className={styles.inputWithIcon}>
                        <select
                            className={styles.fieldInput}
                            value={sonarLength}
                            onChange={handleUpdateSonar}
                        >
                            <option value="300000">5 Minutes</option>
                            <option value="600000">10 Minutes</option>
                            <option value="1800000">30 Minutes</option>
                            <option value="3600000">1 Hour</option>
                            <option value="7200000">2 Hours</option>
                        </select>
                        <i className="fas fa-clock" />
                    </div>
                </div>

                {/* Timezone */}
                <div className={styles.fieldGroup}>
                    <label className={styles.fieldLabel}>Timezone</label>
                    <div className={styles.inputWithIcon}>
                        <select
                            className={styles.fieldInput}
                            value={selectedTimezone}
                            onChange={handleUpdateTimezone}
                        >
                            {options.map((opt) => (
                                <option key={opt.value} value={opt.value}>
                                    {opt.label}
                                </option>
                            ))}
                        </select>
                        <i className="fas fa-globe" />
                    </div>
                </div>

                {/* Restore QR */}
                <div className={styles.fieldGroup}>
                    <label className={styles.fieldLabel}>Restore QR</label>
                    {deletedQrs?.length > 0 ? (
                        <div className={`${styles.restoreList} ${styles.restoreListScroll}`}>
                            {!canRestore && (
                                <div className={styles.message}>
                                    You have reached your QR capacity. Upgrade to restore more QRs.
                                </div>
                            )}
                            {deletedQrs.map((qr) => (
                                <div key={qr.id} className={styles.restoreItem}>
                                    <div className={styles.restoreThumbnailWrapper}>
                                        <RestoreQRThumbnail qrData={qr.data} size={75} />
                                    </div>
                                    <div className={styles.restoreInfo}>
                                        <div className={styles.restoreName}>{qr?.data?.nickname || "Untitled QR"}</div>
                                        <div className={styles.restoreMeta}>{getTypeHeader(qr?.data?.qr_type)}</div>
                                    </div>
                                    <div className={styles.restoreActions}>
                                        <button
                                            className={styles.primaryButton}
                                            onClick={() => handleRestore(qr.id)}
                                            disabled={!canRestore}
                                        >
                                            Restore
                                        </button>
                                    </div>
                                </div>
                            ))}
                        </div>
                    ) : (
                        <div className={styles.fieldDisplay}>
                            <span>No QRs to restore</span>
                        </div>
                    )}
                </div>

                <AnimatePresence>
                    {message && (
                        <motion.div
                            className={`${styles.message} ${message.type === "success" ? styles.messageSuccess : ""} ${message.type === "error" ? styles.messageError : ""}`}
                            variants={variants}
                            initial="initial"
                            animate="animate"
                            exit="initial"
                            transition={transition}
                        >
                            {message.text}
                        </motion.div>
                    )}
                </AnimatePresence>
            </motion.div>
        </div>
    );
};
